// src/components/charts/ContractorMap.jsx
//
// Dot map showing contractor locations as circle markers plotted at their
// street address (lat/lng from sContractor.latitude / sContractor.longitude).
//
// Map style matches PostcodeMap: CartoDB light_nolabels tiles.
// Dot colour: #1B7A7E — the Contractors Unit Group bar colour.
//
// Props:
//   activityIds  {number[]}  Activity scope
//   orgId        {number}    Org scope fallback
//   filterParams {object}    Platform-wide filter params
//   showReportToggle {bool}
//   onReportToggle   {func}

import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import html2canvas from 'html2canvas';
import { chartApiGet } from '../../lib/chartApi';

// ── Constants ─────────────────────────────────────────────────────────────────

const DOT_COLOR = '#1B7A7E';
const DOT_BORDER = '#0f5558';
const DOT_RADIUS = 7;
const PHI = (1 + Math.sqrt(5)) / 2;
const DEFAULT_RADIUS_KM = 25;
const RADIUS_OPTIONS = [5, 15, 25, 50];

// ── Helpers ───────────────────────────────────────────────────────────────────

// Leaflet LatLngBounds for a km-radius circle centred on a point
function radiusBounds(lat, lng, km) {
  const dLat = km / 111;
  const dLng = km / (111 * Math.cos((lat * Math.PI) / 180));
  return L.latLngBounds([lat - dLat, lng - dLng], [lat + dLat, lng + dLng]);
}

// Zoom level that fits a km radius into a map of mapPxSize pixels.
// Uses the Web Mercator tile formula: at zoom z, 1 tile (256px) = 40075*cos(lat)/2^z km.
function radiusZoom(lat, km, mapPxSize = 600) {
  const latRad = (lat * Math.PI) / 180;
  const kmPerPx = (40075 * Math.cos(latRad)) / 256;
  // We want km*2 (diameter) to fill mapPxSize pixels with a small margin
  const targetKmPerPx = (km * 2) / (mapPxSize * 0.85);
  return Math.floor(Math.log2(kmPerPx / targetKmPerPx));
}

// ── Component ─────────────────────────────────────────────────────────────────

const ContractorMap = ({
  activityIds = [],
  orgId = null,
  filterParams = {},
  showReportToggle = false,
  onReportToggle = null,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [inRange, setInRange] = useState([]);
  const [outOfRange, setOutOfRange] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const [radius, setRadius] = useState(DEFAULT_RADIUS_KM);
  const radiusRef = useRef(DEFAULT_RADIUS_KM);

  const [showLabels, setShowLabels] = useState(false);
  const labelsLayerRef = useRef(null);

  const [viewMode, setViewMode] = useState('interactive');
  const [included, setIncluded] = useState(false);
  const [tableIncluded, setTableIncluded] = useState(false);
  const [savingPng, setSavingPng] = useState(false);
  const reportAreaRef = useRef(null);

  const captureRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapInnerRef = useRef(null);
  const [mapW, setMapW] = useState(680);
  const mapWRef = useRef(680);
  const mapH = Math.round(mapW / PHI);

  const mapDivRef = useRef(null);
  const leafletRef = useRef(null);
  const updateOutOfRangeRef = useRef(null);
  const dataRef = useRef(null);
  const markersRef = useRef(new Map()); // contractorId → L.circleMarker
  const radiusCircleRef = useRef(null);
  const resizeTimerRef = useRef(null);
  const boundsRef = useRef(null);

  const isReport = viewMode === 'report';

  useEffect(() => {
    mapWRef.current = mapW;
  }, [mapW]);

  // ── Measure container width ────────────────────────────────────────────────
  useEffect(() => {
    const el = mapContainerRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) {
        const next = Math.min(860, Math.max(300, w));
        setMapW(next);
        mapWRef.current = next;
      }
    };
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    measure();
    return () => obs.disconnect();
  }, []);

  // ── Refit Leaflet on resize ────────────────────────────────────────────────
  // Only invalidate size — never change zoom/center here. The markers effect
  // sets the view on load/radius change; handleSavePng resets it before export.
  useEffect(() => {
    const map = leafletRef.current;
    if (!map) return;
    clearTimeout(resizeTimerRef.current);
    resizeTimerRef.current = setTimeout(() => {
      map.invalidateSize();
    }, 150);
    return () => clearTimeout(resizeTimerRef.current);
  }, [mapW, mapH]);

  // ── Fetch data ─────────────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    setError(null);
    setData(null);
    setSelectedId(null);

    const params = { ...(filterParams || {}) };
    if (activityIds?.length > 0) params.activityIds = activityIds.join(',');
    else if (orgId) params.orgId = orgId;

    chartApiGet('contractor-locations', params)
      .then((d) => {
        dataRef.current = d;
        setData(d);
      })
      .catch((e) => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
  }, [activityIds?.join(','), orgId, JSON.stringify(filterParams)]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── All geocoded contractors go on the map; split by viewport for the table ──
  useEffect(() => {
    if (!data) return;
    setInRange(data.contractors ?? []);
  }, [data]);

  // Recompute out-of-viewport list from current map bounds.
  // Stored in a ref so the map event listener always calls the latest version.
  const updateOutOfRange = useCallback(() => {
    const map = leafletRef.current;
    if (!map || !data) return;
    const bounds = map.getBounds();
    const contractors = data.contractors ?? [];
    const out = contractors
      .filter((c) => !bounds.contains([c.lat, c.lng]))
      .sort((a, b) => (a.occupation || '').localeCompare(b.occupation || ''));
    setOutOfRange(out);
  }, [data]);
  updateOutOfRangeRef.current = updateOutOfRange;

  // ── Initialise Leaflet ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapDivRef.current || leafletRef.current) return;
    const map = L.map(mapDivRef.current, {
      center: [-36.5, 144.5],
      zoom: 6,
      zoomControl: false,
      scrollWheelZoom: false,
    });
    L.tileLayer(
      `https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png?key=${
        import.meta.env.REACT_APP_CARTO_API_KEY
      }`,
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 19,
      }
    ).addTo(map);
    map.on('click', () => setSelectedId(null));
    map.on('moveend zoomend', () => updateOutOfRangeRef.current?.());
    mapDivRef.current.addEventListener('click', () =>
      map.scrollWheelZoom.enable()
    );
    mapDivRef.current.addEventListener('mouseleave', () =>
      map.scrollWheelZoom.disable()
    );
    leafletRef.current = map;
    return () => {
      map.remove();
      leafletRef.current = null;
    };
  }, []);

  // ── Place labels overlay ───────────────────────────────────────────────────
  useEffect(() => {
    const map = leafletRef.current;
    if (!map) return;
    if (showLabels) {
      if (!labelsLayerRef.current) {
        labelsLayerRef.current = L.tileLayer(
          `https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png?key=${
            import.meta.env.REACT_APP_CARTO_API_KEY
          }`,
          { subdomains: 'abcd', maxZoom: 19, pane: 'shadowPane' }
        );
      }
      labelsLayerRef.current.addTo(map);
    } else {
      labelsLayerRef.current?.remove();
    }
  }, [showLabels]);

  // ── Plot markers and set org-centred radius view when data arrives ──────────
  useEffect(() => {
    const map = leafletRef.current;
    if (!map || !data) return;

    // Clear old markers and radius circle
    markersRef.current.forEach((m) => m.remove());
    markersRef.current.clear();
    radiusCircleRef.current?.remove();
    radiusCircleRef.current = null;
    boundsRef.current = null;

    const { orgLat, orgLng } = data;
    const hasOrgLocation = orgLat != null && orgLng != null;

    // Draw a subtle dashed circle showing the radius boundary
    if (hasOrgLocation) {
      radiusCircleRef.current = L.circle([orgLat, orgLng], {
        radius: radius * 1000,
        color: '#9ca3af',
        weight: 1,
        dashArray: '5 5',
        fillOpacity: 0,
        interactive: false,
      }).addTo(map);
    }

    // Plot in-range contractors as dots
    inRange.forEach((c) => {
      const marker = L.circleMarker([c.lat, c.lng], {
        radius: DOT_RADIUS,
        color: DOT_BORDER,
        weight: 1.5,
        fillColor: DOT_COLOR,
        fillOpacity: 0.75,
        opacity: 1,
      });

      marker.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        setSelectedId((prev) => (prev === c.id ? null : c.id));
      });
      marker.on('mouseover', function () {
        this.setStyle({ radius: DOT_RADIUS + 2, fillOpacity: 1, weight: 2 });
        this.bringToFront();
      });
      marker.on('mouseout', function () {
        this.setStyle({ radius: DOT_RADIUS, fillOpacity: 0.75, weight: 1.5 });
      });

      marker.addTo(map);
      markersRef.current.set(c.id, marker);
    });

    // Deferred setView/fitBounds must be cancelled on cleanup — otherwise a
    // fast unmount (StrictMode's dev-mode double-invoke, or the user
    // collapsing this map right after data arrives) leaves this timeout
    // pending, and it later fires against a Leaflet instance whose panes
    // `.remove()` already tore down, throwing deep inside Leaflet
    // ("Cannot read properties of undefined (reading '_leaflet_pos')").
    // The `leafletRef.current !== map` guard is a second line of defence for
    // the case where the timeout still fires in the gap between a new map
    // being created and this callback running.
    let timeoutId;
    if (hasOrgLocation) {
      const bounds = radiusBounds(orgLat, orgLng, radius);
      boundsRef.current = bounds;
      const zoom = radiusZoom(orgLat, radius, Math.min(mapWRef.current, 600));
      map.invalidateSize();
      timeoutId = setTimeout(() => {
        if (leafletRef.current !== map) return;
        map.setView([orgLat, orgLng], zoom, { animate: false });
        // setView is synchronous with animate:false, so bounds are immediately correct
        updateOutOfRangeRef.current?.();
      }, 50);
    } else if (inRange.length > 0) {
      const bounds = L.latLngBounds(inRange.map((c) => [c.lat, c.lng]));
      boundsRef.current = bounds;
      map.invalidateSize();
      timeoutId = setTimeout(() => {
        if (leafletRef.current !== map) return;
        map.fitBounds(bounds, { animate: false, padding: [24, 24] });
        updateOutOfRangeRef.current?.();
      }, 50);
    }
    return () => clearTimeout(timeoutId);
  }, [data, inRange, radius]);

  // ── Highlight selected marker ──────────────────────────────────────────────
  useEffect(() => {
    markersRef.current.forEach((marker, id) => {
      if (id === selectedId) {
        marker.setStyle({
          radius: DOT_RADIUS + 3,
          fillOpacity: 1,
          color: '#ffffff',
          weight: 2,
        });
        marker.bringToFront();
      } else {
        marker.setStyle({
          radius: DOT_RADIUS,
          fillOpacity: 0.75,
          color: DOT_BORDER,
          weight: 1.5,
        });
      }
    });
  }, [selectedId]);

  // ── PNG export ─────────────────────────────────────────────────────────────
  const capturePng = useCallback(
    () =>
      html2canvas(captureRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      }).then((canvas) => canvas.toDataURL('image/png')),
    []
  );

  const handleSavePng = useCallback(async () => {
    setSavingPng(true);
    try {
      const dataUrl = await capturePng();
      const link = document.createElement('a');
      link.download = 'contractor-locations.png';
      link.href = dataUrl;
      link.click();
    } finally {
      setSavingPng(false);
    }
  }, [capturePng]);

  const handleReportToggle = useCallback(
    async (value) => {
      setIncluded(value);
      if (value) {
        setViewMode('report');
        setTimeout(async () => {
          try {
            const dataUrl = await capturePng();
            if (onReportToggle) onReportToggle(true, dataUrl);
          } catch (_) {
            if (onReportToggle) onReportToggle(true, null);
          }
        }, 600);
      } else {
        if (onReportToggle) onReportToggle(false, null);
      }
    },
    [capturePng, onReportToggle]
  );

  const selected = selectedId ? inRange.find((c) => c.id === selectedId) : null;
  const total = inRange.length;
  const mappedCount = (data?.contractors ?? []).length;
  const totalContractors = data?.totalContractors ?? mappedCount;
  const unmappedCount = totalContractors - mappedCount;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="pm-wrap">
      {loading && (
        <div className="pm-loading">{t('contractors.loading_locations')}…</div>
      )}
      {error && (
        <div className="pm-error">
          {t('common.error')}: {error}
        </div>
      )}
      {!loading && !error && data && total === 0 && (
        <div className="pm-empty">
          {t('contractors.no_contractors_with_geocoded_address')}
        </div>
      )}

      {/* ── Toolbar ── */}
      {showReportToggle && (
        <div className="pm-toolbar">
          <div className="pm-tabs">
            <button
              type="button"
              className={`pm-tabs__tab${
                !isReport ? ' pm-tabs__tab--active' : ''
              }`}
              onClick={() => setViewMode('interactive')}
            >
              {t('maps.interactive')}
            </button>
            <button
              type="button"
              className={`pm-tabs__tab${
                isReport ? ' pm-tabs__tab--active' : ''
              }`}
              onClick={() => setViewMode('report')}
            >
              {t('maps.report_view')}
            </button>
          </div>

          {isReport && (
            <button
              className="ch__save-png"
              onClick={handleSavePng}
              disabled={savingPng}
            >
              {savingPng
                ? `${t('common.saving')}…`
                : `⬇ {t('common.save_png')}`}
            </button>
          )}

          {!isReport && (
            <div className="ch__include">
              <span className="ch__include-label">
                {t('report.include_in_report')}
              </span>
              <div className="ch__include-btns">
                <button
                  className={`ch__include-btn ch__include-btn--yes ${
                    included
                      ? 'ch__include-btn--active'
                      : 'ch__include-btn--inactive'
                  }`}
                  onClick={() => {
                    if (!included) handleReportToggle(true);
                  }}
                >
                  {t('actions.yes')}
                </button>
                <button
                  className={`ch__include-btn ${
                    !included
                      ? 'ch__include-btn--active'
                      : 'ch__include-btn--inactive'
                  }`}
                  onClick={() => {
                    if (included) handleReportToggle(false);
                  }}
                >
                  {t('actions.no')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Map + legend ── */}
      <div ref={reportAreaRef} className={isReport ? 'pm-report-area' : ''}>
        {/* ── Capture wrapper: stats heading + map (PNG export target) ── */}
        <div ref={captureRef}>
          {/* ── Stats ── */}
          {data && (
            <div className="pm-stats">
              {total > 0 && (
                <span>
                  <Trans
                    i18nKey="contractors.num_contractors_mapped"
                    values={{
                      total,
                      contractors: t('contractors.num_contractors', {
                        count: total,
                      }),
                    }}
                  />
                </span>
              )}
              {outOfRange.length > 0 && (
                <span>
                  <Trans
                    i18nKey="contractors.num_outside_map_view"
                    values={{ numContractors: outOfRange.length }}
                  />
                </span>
              )}
              {unmappedCount > 0 && (
                <span className="pm-stats__note">
                  {t('contractors.without_map_address', {
                    numContractors: t('contractors.num_contractors', {
                      count: unmappedCount,
                    }),
                  })}
                </span>
              )}
            </div>
          )}

          {/* ── Map options ── */}
          {!isReport && (
            <div className="pm-options">
              <span className="pm-options__heading">
                {t('contractors.map_options')}
              </span>
              <div className="pm-options__row">
                <span className="pm-options__label">
                  {t('contractors.place_labels')}
                </span>
                <div className="ch__include-btns">
                  <button
                    type="button"
                    className={`ch__include-btn ch__include-btn--yes ${
                      showLabels
                        ? 'ch__include-btn--active'
                        : 'ch__include-btn--inactive'
                    }`}
                    onClick={() => setShowLabels(true)}
                  >
                    {t('actions.yes')}
                  </button>
                  <button
                    type="button"
                    className={`ch__include-btn ${
                      !showLabels
                        ? 'ch__include-btn--active'
                        : 'ch__include-btn--inactive'
                    }`}
                    onClick={() => setShowLabels(false)}
                  >
                    {t('actions.no')}
                  </button>
                </div>
              </div>
              <div className="pm-options__row">
                <span className="pm-options__label">
                  {t('contractors.radius')}
                </span>
                <select
                  className="pm-options__select"
                  value={radius}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    radiusRef.current = v;
                    setRadius(v);
                  }}
                >
                  {RADIUS_OPTIONS.map((km) => (
                    <option key={km} value={km}>
                      {km} km
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div ref={mapContainerRef} className="pm-container">
            <div
              ref={mapInnerRef}
              style={{
                position: 'relative',
                width: mapW,
                height: mapH,
                background: '#e8ecf0',
                borderRadius: isReport ? 0 : 4,
                overflow: 'hidden',
              }}
            >
              <div ref={mapDivRef} style={{ position: 'absolute', inset: 0 }} />

              {!isReport && (
                <div className="pm-zoom">
                  <button
                    type="button"
                    onClick={() => leafletRef.current?.zoomIn()}
                  >
                    +
                  </button>
                  <button
                    type="button"
                    onClick={() => leafletRef.current?.zoomOut()}
                  >
                    −
                  </button>
                </div>
              )}

              {/* Legend swatch in bottom-left corner */}
              {data && total > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 8,
                    left: 8,
                    background: 'rgba(255,255,255,0.88)',
                    borderRadius: 4,
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 11,
                    color: '#374151',
                    zIndex: 1000,
                    pointerEvents: 'none',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: DOT_COLOR,
                      border: `1.5px solid ${DOT_BORDER}`,
                      flexShrink: 0,
                    }}
                  />
                  {t('contractors.contractor_address')}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* end captureRef */}

        {/* ── Selected contractor detail panel ── */}
        {!isReport && selected && (
          <div className="pm-detail">
            <div
              className="pm-detail__swatch"
              style={{ background: DOT_COLOR, borderRadius: '50%' }}
            />
            <div className="pm-detail__info">
              <span className="pm-detail__code">{selected.name}</span>
              {(selected.suburb || selected.state) && (
                <span className="pm-detail__locality">
                  {[selected.suburb, selected.state].filter(Boolean).join(', ')}
                  {selected.postcode ? ` ${selected.postcode}` : ''}
                </span>
              )}
            </div>
            <button
              type="button"
              className="pm-detail__close"
              onClick={() => setSelectedId(null)}
            >
              ×
            </button>
          </div>
        )}

        {/* ── Out-of-range contractor table ── */}
        {outOfRange.length > 0 && (
          <div className="cm-out-of-range">
            <div className="cm-out-of-range__heading">
              {t('contractors.outside_map_view')}
              {showReportToggle && (
                <div className="ch__include cm-out-of-range__include">
                  <span className="ch__include-label">
                    {t('report.include_in_report')}
                  </span>
                  <div className="ch__include-btns">
                    <button
                      className={`ch__include-btn ch__include-btn--yes ${
                        tableIncluded
                          ? 'ch__include-btn--active'
                          : 'ch__include-btn--inactive'
                      }`}
                      onClick={() => setTableIncluded(true)}
                    >
                      {t('actions.yes')}
                    </button>
                    <button
                      className={`ch__include-btn ${
                        !tableIncluded
                          ? 'ch__include-btn--active'
                          : 'ch__include-btn--inactive'
                      }`}
                      onClick={() => setTableIncluded(false)}
                    >
                      {t('actions.no')}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <table className="cm-out-of-range__table">
              <thead>
                <tr>
                  <th>{t('contractors.occupation')}</th>
                  <th>{t('contractors.location')}</th>
                </tr>
              </thead>
              <tbody>
                {outOfRange.map((c, i) => {
                  const location = [c.localityName || c.suburb, c.state]
                    .filter(Boolean)
                    .join(', ');
                  return (
                    <tr
                      key={c.id}
                      className={
                        i % 2 === 0 ? 'cm-out-of-range__row--even' : ''
                      }
                    >
                      <td>{c.occupation || c.name || '—'}</td>
                      <td>{location || c.localityName || c.postcode || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractorMap;
